import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { fetchAuthSession } from "aws-amplify/auth";
import { authFetch } from "./api";
import CenterTable from "./components/CenterTable";
import CenterForm from "./components/CenterForm";
import FootfallEntry from "./components/FootfallEntry";
import SiteAssignments from "./components/SiteAssignments";
import FootfallDashboard from "./components/FootfallDashboard";
import ExitDashboard from "./components/ExitDashboard";
import MISReport from "./components/MISReport";
import DailyLog from "./components/DailyLog";
import ExitLog from "./components/ExitLog";
import AnalyticsDashboard from "./components/AnalyticsDashboard";
import UserManagement from "./components/UserManagement";

const API_BASE = process.env.REACT_APP_API_URL;

export default function App() {
  return (
    <Authenticator hideSignUp={true}>
      {({ signOut, user }) => (
        <AdminShell user={user} signOut={signOut} />
      )}
    </Authenticator>
  );
}

function AdminShell({ user, signOut }) {
  const navigate      = useNavigate();
  const { pathname }  = useLocation();
  const [centers, setCenters]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [showForm, setShowForm]             = useState(false);
  const [editTarget, setEditTarget]         = useState(null);
  const [notice, setNotice]                 = useState(null);
  const [userGroups, setUserGroups]         = useState([]);
  const [footfallCenter, setFootfallCenter] = useState(null);
  const [showAssignments, setShowAssignments]   = useState(false);
  const [showDashboard, setShowDashboard]         = useState(false);
  const [showExitDashboard, setShowExitDashboard] = useState(false);
  const [showMIS, setShowMIS]                     = useState(false);
  const [showAnalytics, setShowAnalytics]         = useState(false);
  const [dailyLogCenter, setDailyLogCenter]     = useState(null);
  const [assignedCenter, setAssignedCenter] = useState(null);

  const userEmail = user?.signInDetails?.loginId;

  useEffect(() => {
    fetchAuthSession().then((session) => {
      const groups = session.tokens?.idToken?.payload["cognito:groups"] ?? [];
      setUserGroups(groups);
    });
  }, [user]);

  const isAdmin      = userGroups.includes("admin");
  const isSuperAdmin = userGroups.includes("super_admin");
  const isSiteAdmin  = !isAdmin && !isSuperAdmin && userGroups.includes("site_admin");

  // Fetch assigned center for site_admin users
  useEffect(() => {
    if (!isSiteAdmin || !userEmail) return;
    authFetch(`${API_BASE}/admin/assignments/me`)
      .then(r => r.ok ? r.json() : null)
      .then(assignment => {
        if (assignment) {
          // Fetch the actual center object
          authFetch(`${API_BASE}/centers/${assignment.center_id}`)
            .then(r => r.ok ? r.json() : null)
            .then(center => setAssignedCenter(center));
        }
      })
      .catch(() => {});
  }, [isSiteAdmin, userEmail]);

  const fetchCenters = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/centers`);
      if (!res.ok) throw new Error("Failed to load centers");
      setCenters(await res.json());
    } catch (e) {
      setNotice({ type: "error", text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCenters(); }, []);

  const handleEdit = (center) => {
    setEditTarget(center);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditTarget(null);
    setShowForm(true);
  };

  const handleSave = (msg) => {
    setShowForm(false);
    setEditTarget(null);
    setNotice({ type: "success", text: msg });
    setTimeout(() => setNotice(null), 4000);
    setTimeout(fetchCenters, 1000);
  };

  // ── Site admin view ───────────────────────────────────────────────────────
  if (isSiteAdmin) {
    return <SiteAdminView center={assignedCenter} userEmail={userEmail} signOut={signOut} />;
  }

  // ── User management page: /users (super_admin only) ───────────────────────
  if (pathname === "/users" && isSuperAdmin) {
    return (
      <UserManagement
        apiBase={API_BASE}
        currentUserEmail={userEmail}
        userEmail={userEmail}
        userGroups={userGroups}
        signOut={signOut}
      />
    );
  }

  // ── Main admin view ───────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh" }}>

      <header style={headerStyle}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Cooling Centers — Admin</div>
          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
            Signed in as {userEmail}
            {userGroups.length > 0 && ` · ${userGroups.join(", ")}`}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {(isAdmin || isSuperAdmin) && (
            <button onClick={() => setShowDashboard(true)} style={btnStyle("secondary-white")}>
              Footfall Dashboard
            </button>
          )}
          {(isAdmin || isSuperAdmin) && (
            <button onClick={() => setShowExitDashboard(true)} style={btnStyle("secondary-white")}>
              Exit Dashboard
            </button>
          )}
          {(isAdmin || isSuperAdmin) && (
            <button onClick={() => setShowMIS(true)} style={btnStyle("secondary-white")}>
              MIS Report
            </button>
          )}
          {(isAdmin || isSuperAdmin) && (
            <button onClick={() => setShowAnalytics(true)} style={btnStyle("secondary-white")}>
              Analytics & Feedback
            </button>
          )}
          {(isAdmin || isSuperAdmin) && (
            <button onClick={() => setShowAssignments(true)} style={btnStyle("secondary-white")}>
              Manage Assignments
            </button>
          )}
          {isSuperAdmin && (
            <button onClick={() => navigate("/users")} style={btnStyle("secondary-white")}>
              Users
            </button>
          )}
          <button onClick={signOut} style={btnStyle("outline")}>Sign out</button>
        </div>
      </header>

      <main style={{ maxWidth: 960, margin: "0 auto", padding: "24px 16px" }}>

        {notice && (
          <div style={{
            background: notice.type === "error" ? "#fdecea" : "#e0f3fc",
            border: `1px solid ${notice.type === "error" ? "#f5c6c2" : "#6dceff"}`,
            borderRadius: 8, padding: "10px 16px",
            marginBottom: 16, fontSize: 14,
            color: notice.type === "error" ? "#8B2020" : "#004d74",
          }}>
            {notice.text}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: "#1a1a1a" }}>
            {loading ? "Loading…" : `${centers.length} centers`}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={fetchCenters} style={btnStyle("secondary")}>Refresh</button>
            {(isAdmin || isSuperAdmin) && (
              <button onClick={handleAdd} style={btnStyle("primary")}>+ Add center</button>
            )}
          </div>
        </div>

        {!loading && (
          <CenterTable
            centers={centers}
            onEdit={handleEdit}
            onLogEntry={setFootfallCenter}
            onDailyLog={setDailyLogCenter}
            canEdit={isSuperAdmin}
          />
        )}
      </main>

      {showForm && (
        <CenterForm
          center={editTarget}
          onSave={handleSave}
          onClose={() => setShowForm(false)}
        />
      )}

      {footfallCenter && (
        <FootfallEntry
          center={footfallCenter}
          userEmail={userEmail}
          apiBase={API_BASE}
          onClose={() => setFootfallCenter(null)}
        />
      )}

      {showAssignments && (
        <SiteAssignments
          centers={centers}
          apiBase={API_BASE}
          onClose={() => setShowAssignments(false)}
        />
      )}

      {showDashboard && (
        <FootfallDashboard
          centers={centers}
          apiBase={API_BASE}
          onClose={() => setShowDashboard(false)}
        />
      )}

      {showExitDashboard && (
        <ExitDashboard
          centers={centers}
          apiBase={API_BASE}
          onClose={() => setShowExitDashboard(false)}
        />
      )}

      {showMIS && (
        <MISReport
          centers={centers}
          apiBase={API_BASE}
          onClose={() => setShowMIS(false)}
        />
      )}

      {showAnalytics && (
        <AnalyticsDashboard
          apiBase={API_BASE}
          onClose={() => setShowAnalytics(false)}
        />
      )}

      {dailyLogCenter && (
        <DailyLog
          center={dailyLogCenter}
          userEmail={userEmail}
          apiBase={API_BASE}
          onClose={() => setDailyLogCenter(null)}
        />
      )}

    </div>
  );
}

const headerStyle = {
  background: "#004d74", color: "#fff",
  padding: "14px 24px", display: "flex",
  alignItems: "center", justifyContent: "space-between",
};

function SiteAdminView({ center, userEmail, signOut }) {
  const [tab, setTab] = useState("log");

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      <header style={headerStyle}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            {center ? center.name : "Cooling Centers"}
          </div>
          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>{userEmail}</div>
        </div>
        <button onClick={signOut} style={btnStyle("outline")}>Sign out</button>
      </header>

      {!center ? (
        <div style={{ textAlign: "center", color: "#888", marginTop: 80, fontSize: 15 }}>
          No site assigned to your account yet.<br />Contact your administrator.
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", background: "#fff", borderBottom: "1px solid #e5e5e5" }}>
            <button onClick={() => setTab("log")} style={tabStyle(tab === "log", "entry")}>
              <span style={{ fontSize: 15, fontWeight: 700 }}>Log Entry & Exit</span>
              <span style={{ fontSize: 11, opacity: 0.75 }}>Entry and exit on one screen</span>
            </button>
            <button onClick={() => setTab("daily")} style={tabStyle(tab === "daily", "daily")}>
              <span style={{ fontSize: 15, fontWeight: 700 }}>📋 Daily Log</span>
              <span style={{ fontSize: 11, opacity: 0.75 }}>Supplies & training</span>
            </button>
          </div>

          <main style={{ maxWidth: 960, margin: "24px auto", padding: "0 16px" }}>
            {tab === "log" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <FootfallEntry center={center} userEmail={userEmail} apiBase={API_BASE} onClose={null} />
                <ExitLog center={center} apiBase={API_BASE} />
              </div>
            )}
            {tab === "daily" && (
              <div style={{ maxWidth: 480, margin: "0 auto" }}>
                <DailyLog center={center} userEmail={userEmail} apiBase={API_BASE} onClose={null} />
              </div>
            )}
          </main>
        </>
      )}
    </div>
  );
}

function tabStyle(active, type) {
  const colors = {
    entry: { active: "#1a9e4a", border: "#1a9e4a", bg: "#f0faf4" },
    exit:  { active: "#d94f2b", border: "#d94f2b", bg: "#fff5f3" },
    daily: { active: "#0093dd", border: "#0093dd", bg: "#f0f8ff" },
  };
  const c = colors[type];
  return {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
    padding: "10px 4px", border: "none", cursor: "pointer",
    background: active ? c.bg : "#fff",
    color: active ? c.active : "#777",
    borderBottom: active ? `3px solid ${c.border}` : "3px solid transparent",
    transition: "all 0.15s",
  };
}

function btnStyle(variant) {
  const base = {
    border: "none", borderRadius: 8, padding: "8px 16px",
    fontSize: 13, fontWeight: 500, cursor: "pointer",
  };
  if (variant === "primary")        return { ...base, background: "#0093dd", color: "#fff" };
  if (variant === "secondary")      return { ...base, background: "#f0f0ee", color: "#333" };
  if (variant === "secondary-white") return { ...base, background: "rgba(255,255,255,0.15)", color: "#fff" };
  if (variant === "outline")        return { ...base, background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.5)" };
  return base;
}
